// Global
import { Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React from 'react';

// Lib
import { ComponentProps } from 'lib/component-props';
import { useTheme } from 'lib/context/ThemeContext';
import { HeroComponents } from 'lib/templates/Feature.Dart.model';
import { ComponentVariants } from 'lib/context/ComponentVariants';
import { useI18n } from 'next-localization';

// Local
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import Button from 'helpers/Button/Button';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import SVG from 'helpers/SVG/SVG';
import LegalDisclaimer from 'helpers/LegalDisclaimer/LegalDisclaimer';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import fallback from 'lib/fallback/fallback';
import { tailwindVariants, tailwindVariantsForWithImageVariant } from './HeroInternalTailwind';
//import { DartPages } from 'lib/templates/Project.Dart.model';

export type HeroInternalProps = ComponentProps &
  HeroComponents.HeroInternal.HeroInternal & {
    theme?: string;
  };

type Alignment = 'Left' | 'Center' | undefined;

type ShowPattern = true | false | undefined;

const HeroInternal = (props: HeroInternalProps): JSX.Element => {
  const {
    description,
    enablePattern,
    primaryCTA,
    title,
    disclaimerText,
    primaryCTAColor,
    primaryCTAType,
  } = props?.fields || {};
  const { alignContent } = props?.params || {};
  const { dataSource, componentName } = props?.rendering || {};
  const i18n = useI18n();

  const { themeName } = useTheme();
  const componentVariants = ComponentVariants();

  const alignment = alignContent !== 'Left' && 'Center';
  const showPattern = enablePattern?.value ? true : false;

  const {
    base,
    titleText,
    descriptionText,
    svgBackground,
    svgDecor,
    textWrapper,
    contentCtaWrapper,
    disclaimerClasses,
  } = tailwindVariants({
    alignment: alignment as Alignment,
    showPattern: showPattern as ShowPattern,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
  });
  const { sitecoreContext } = useSitecoreContext();
  if (!props?.fields) return <></>;

  const lastUpdatedText = i18n.t('lastUpdated') ? i18n.t('lastUpdated') : 'Last Updated';
  const publishDate = sitecoreContext?.route?.fields?.publishDate as Field<string>;

  // Define the shape of date format config
  interface DateFormatConfig {
    format: string;
    separator: string;
    trailingSeparator: string; // "1" = true, "0" = false
  }

  const formattedDate = new Date(publishDate?.value);

  // Safe fallback if date format config missing
  const dateFormatConfig: DateFormatConfig = (sitecoreContext?.dateFormat as DateFormatConfig) || {
    format: 'dd-MM-yyyy',
    separator: '-',
    trailingSeparator: '0',
  };

  const datePattern = dateFormatConfig.format || 'dd-MM-yyyy';
  const separatorPattern = dateFormatConfig.separator || '-';
  const trailingSeparator = dateFormatConfig.trailingSeparator === '1';

  function formatDate(
    date: Date | string | null | undefined,
    format: string,
    separator: string,
    addTrailing: boolean
  ): string {
    // Handle empty or null date gracefully
    if (!date) {
      return 'N/A';
    }

    // Normalize date input
    if (!(date instanceof Date)) {
      date = new Date(date);
    }

    // Fallback for invalid date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided:', date);
      return 'Invalid Date';
    }

    // Prepare parts
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());

    // Replace format tokens
    let formattedDate = format.replace('dd', day).replace('MM', month).replace('yyyy', year);

    // Normalize separators (replace -, /, or .)
    formattedDate = formattedDate.replace(/[-/.]/g, separator);

    // Add trailing separator if configured
    if (addTrailing && !formattedDate.endsWith(separator)) {
      formattedDate += separator;
    }

    return formattedDate;
  }

  // Final safe formatted date
  const finalDate = formatDate(formattedDate, datePattern, separatorPattern, trailingSeparator);

  return (
    <Container fullWidth>
      <div className={base()} data-component="authorable/heroes/internalhero">
        {componentVariants?.heroInternal?.hasDecor &&
          (themeName == 'Off' || themeName == 'Autan') && (
            <SVG className={svgDecor()} svg={`hero-internal/Decor,Brand=${themeName}`} />
          )}
        <SVG className={svgBackground()} svg={`hero-internal/BG,Brand=${themeName}`} />
        <div className={textWrapper()}>
          <Text className={titleText()} encode={false} field={title} tag="h1" />

          {sitecoreContext?.route?.templateId === '7e22696f-482e-4f40-8c42-0881c4059e7f' &&
            publishDate?.value !== '0001-01-01T00:00:00Z' && (
              <div className={descriptionText()}>
                {lastUpdatedText}: {finalDate}
              </div>
            )}
          {props.fields?.description && (
            <RichTextA11yWrapper className={descriptionText()} field={description} />
          )}
          {primaryCTA?.value?.href && (
            <div className={contentCtaWrapper()}>
              <Button
                href={primaryCTA?.value?.href}
                linktype={primaryCTA?.value?.linktype}
                label={primaryCTA?.value?.text}
                tag="a"
                target={primaryCTA?.value?.target}
                color={primaryCTAColor?.value || 'white'}
                type={primaryCTAType?.value || 'filled'}
                size="compressed"
                gtmEvent={{
                  event: 'cta_click',
                  type: 'primary',
                  'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
                  'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
                  'gtm.element.dataset.gtmDatasourceId': dataSource,
                  'gtm.element.dataset.gtmComponentName': componentName,
                }}
              >
                <LinkWrapper field={primaryCTA} />
              </Button>
            </div>
          )}
          {disclaimerText?.value != '' && (
            <LegalDisclaimer
              disclaimerText={disclaimerText}
              disclaimerClasses={disclaimerClasses()}
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export const WithImage = (props: HeroInternalProps): JSX.Element => {
  const {
    description,
    primaryCTA,
    secondaryCTA,
    title,
    disclaimerText,
    primaryCTAColor,
    primaryCTAType,
    secondaryCTAColor,
    secondaryCTAType,
    subtitle,
    logo,
    image,
  } = props?.fields || {};
  const { dataSource, componentName } = props?.rendering || {};

  const { themeName } = useTheme();

  // Add fallback component variant color
  const fallbackComponentVariantColor = fallback?.topBar?.value;
  const fallbackComponentVariantType = fallback?.topBar?.type;

  const {
    base,
    leftSideContentWrapper,
    logoWrapper,
    contentWrapper,
    contentTitle,
    contentSubtitle,
    contentDescription,
    contentCtaWrapper,
    primaryCtaWrapper,
    secondaryCtaWrapper,
    disclaimerClasses,
    rightSideImageWrapper,
    imageClasses,
  } = tailwindVariantsForWithImageVariant({
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
  });

  if (!props?.fields) return <></>;

  return (
    <Container fullWidth>
      <div className={base()} data-component="authorable/heroes/internalheroWithImage">
        <div className={leftSideContentWrapper()}>
          {logo?.value?.src && (
            <div className={logoWrapper()}>
              <ImageWrapper field={logo} />
            </div>
          )}
          <div className={contentWrapper()}>
            {title?.value && (
              <Text className={contentTitle()} encode={false} field={title} tag="h1" />
            )}
            {subtitle?.value && (
              <Text className={contentSubtitle()} encode={false} field={subtitle} tag="p" />
            )}
            {description?.value && (
              <RichTextA11yWrapper className={contentDescription()} field={description} />
            )}
          </div>
          <div className={contentCtaWrapper()}>
            {primaryCTA?.value?.text && (
              <div className={primaryCtaWrapper()}>
                <Button
                  href={primaryCTA?.value?.href}
                  linktype={primaryCTA?.value?.linktype}
                  target={primaryCTA?.value?.target}
                  label={primaryCTA?.value?.text}
                  tag="a"
                  fullWidth
                  // As the design requires a filled CTA, we do not need to add a fallback since
                  // the button component has a default variant that displays a filled CTA
                  color={primaryCTAColor?.value || fallbackComponentVariantColor}
                  type={primaryCTAType?.value || 'filled'}
                  gtmEvent={{
                    event: 'cta_click',
                    type: 'primary',
                    'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
                    'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
                    'gtm.element.dataset.gtmDatasourceId': dataSource,
                    'gtm.element.dataset.gtmComponentName': componentName,
                  }}
                />
              </div>
            )}
            {secondaryCTA?.value?.text && (
              <div className={secondaryCtaWrapper()}>
                <Button
                  href={secondaryCTA?.value?.href}
                  linktype={secondaryCTA?.value?.linktype}
                  target={secondaryCTA?.value?.target}
                  label={secondaryCTA?.value?.text}
                  tag="a"
                  fullWidth
                  // The design requires an outline CTA, so for that we have added a fallback as an outline value,
                  // so if there is no value in sitecore field, it will take the outline value.
                  type={secondaryCTAType?.value || fallbackComponentVariantType}
                  color={secondaryCTAColor?.value || fallbackComponentVariantColor}
                  gtmEvent={{
                    event: 'cta_click',
                    type: 'secondary',
                    'gtm.element.dataset.gtmLinkUrl': secondaryCTA?.value?.href,
                    'gtm.element.dataset.gtmLinkName': secondaryCTA?.value?.text,
                    'gtm.element.dataset.gtmDatasourceId': dataSource,
                    'gtm.element.dataset.gtmComponentName': componentName,
                  }}
                />
              </div>
            )}
          </div>
          {disclaimerText?.value != '' && (
            <LegalDisclaimer
              disclaimerText={disclaimerText}
              disclaimerClasses={disclaimerClasses()}
            />
          )}
        </div>
        <div className={rightSideImageWrapper()}>
          <ImageWrapper field={image} className={imageClasses()} />
        </div>
      </div>
    </Container>
  );
};

export default HeroInternal;
